/**
 * Typed client factory for the capability AI assist endpoints
 * (Phase F-finale-4 Slice 5).
 *
 * Lives in `@eduviaos/shared` so both the SA and IA portals build their
 * client from the same factory — each passes its own `ApiClient` singleton
 * (which carries the portal-specific base URL + tenant-slug provider), so
 * tenant isolation is preserved without this module knowing which portal
 * called it (Iron Rule #15).
 *
 * The factory is intentionally thin: it adds no caching or retry logic. The
 * `ApiClient` already attaches auth + tenant headers, refreshes on 401, and
 * unwraps errors into `ApiError`. TanStack Query wiring lives per-app
 * (Slice 6), consistent with the existing domain-client convention.
 */

import type {
  ApplyRequest,
  ApplyResult,
  CapabilitySuggestion,
  SuggestRequest,
} from "../types/capabilityAi.js";
import type { ApiClient } from "./client.js";

const BASE = "/ai/capability";

/** Typed methods over the three capability AI endpoints. */
export interface CapabilityAiClient {
  /** `POST /ai/capability/suggest` — generate + persist one suggestion. */
  requestSuggestion(req: SuggestRequest): Promise<CapabilitySuggestion>;
  /** `GET /ai/capability/suggestions/{id}` — read one back. */
  getSuggestion(id: string): Promise<CapabilitySuggestion>;
  /** `POST /ai/capability/suggestions/{id}/apply` — selective accept + merge. */
  applySuggestion(id: string, req: ApplyRequest): Promise<ApplyResult>;
}

/**
 * Build a {@link CapabilityAiClient} bound to the given {@link ApiClient}.
 *
 * @param client - The portal's configured API client singleton.
 */
export function makeCapabilityAiClient(client: ApiClient): CapabilityAiClient {
  return makeClientForBase(client, BASE);
}

/**
 * SA-scoped variant (Phase F-finale-4 Slice 6-FE).
 *
 * A platform `super_admin` authors a SPECIFIC tenant's funnel addressed by
 * the `{slug}` path param — the BE routes live under
 * `/super-admin/tenants/{slug}/ai/capability/*` (see
 * `app/domains/capability/routes_ai.py::sa_router`). The target tenant is the
 * path slug, never the SA's `_super_admin` tenant, so the SA `api` singleton
 * (which always sends `X-Tenant-Slug: _super_admin`) can drive these routes
 * unchanged — the path slug is the sole authority for scoping the suggestion
 * and the merge (Iron Rule #15).
 */
export function makeSaCapabilityAiClient(
  client: ApiClient,
  tenantSlug: string,
): CapabilityAiClient {
  const base = `/super-admin/tenants/${encodeURIComponent(tenantSlug)}/ai/capability`;
  return makeClientForBase(client, base);
}

function makeClientForBase(client: ApiClient, base: string): CapabilityAiClient {
  return {
    requestSuggestion: (req) => client.post<CapabilitySuggestion>(`${base}/suggest`, req),
    getSuggestion: (id) =>
      client.get<CapabilitySuggestion>(`${base}/suggestions/${encodeURIComponent(id)}`),
    applySuggestion: (id, req) =>
      client.post<ApplyResult>(`${base}/suggestions/${encodeURIComponent(id)}/apply`, req),
  };
}
